import styled from "@emotion/styled";
import { useCallback, useEffect, useMemo, useState } from "react";

import {
  MDSButton,
  MDSButtonSize,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSSkeleton,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
} from "@/mds";
import { localStorageModule } from "@/modules/local-storage";
import { AgentPageLayout } from "@/pages/agent/components/agent-page-layout";
import { AgentSurfaceNavigationActiveTab } from "@/pages/agent/components/agent-surface-navigation";
import { MemoryAgentDialog } from "@/pages/agent/memory/memory-agent-dialog";
import { MemoryDashboard } from "@/pages/agent/memory/memory-dashboard";
import { MemoryEntityDetailView } from "@/pages/agent/memory/memory-entity-detail";
import { MemoryHome } from "@/pages/agent/memory/memory-home";
import { MemoryLoadingState } from "@/pages/agent/memory/memory-loading-state";
import {
  backfillMemoryManifest,
  getMemoryEntity,
  getMemoryOverview,
  getMemoryView,
  trackCustomerIntelligence,
  type MemoryEntityDetail,
  type MemoryManifest,
  type MemoryOverview,
  type MemoryViewResults,
  updateMemoryManifestStatus,
} from "@/pages/agent/memory/memory-api";
import { useAppStore } from "@/store";

const ACTIVE_RUN_STATUSES = new Set(["queued", "running"]);

export const AgentMemoryPage = () => {
  const { store } = useAppStore();
  const [overview, setOverview] = useState<MemoryOverview | null>(null);
  const [selectedManifestId, setSelectedManifestId] = useState<string | null>(null);
  const [rootView, setRootView] = useState<MemoryViewResults | null>(null);
  const [viewResults, setViewResults] = useState<Record<string, MemoryViewResults>>({});
  const [loadedViewManifestId, setLoadedViewManifestId] = useState<string | null>(null);
  const [selectedViewId, setSelectedViewId] = useState<string | null>(null);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [entityDetail, setEntityDetail] = useState<MemoryEntityDetail | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isStarting, setIsStarting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentDialog, setAgentDialog] = useState<{
    mode: "create" | "customize" | "edit";
    manifest: MemoryManifest | null;
    hasExistingDraft: boolean;
  } | null>(null);
  const [agentChangeBaseline, setAgentChangeBaseline] = useState<string | null>(null);

  const manifests = useMemo(() => overview?.manifests ?? [], [overview?.manifests]);
  const manifest = useMemo(
    () => manifests.find(item => item.id === selectedManifestId) ?? null,
    [manifests, selectedManifestId]
  );
  const activeRun =
    manifest?.latest_run && ACTIVE_RUN_STATUSES.has(manifest.latest_run.status)
      ? manifest.latest_run
      : null;
  const activeRunId = activeRun?.id ?? null;
  const anyActiveRunId =
    manifests.find(item =>
      item.latest_run ? ACTIVE_RUN_STATUSES.has(item.latest_run.status) : false
    )?.latest_run?.id ?? null;
  const manifestRevision = manifests
    .map(item => `${item.id}:${item.updated_at}`)
    .sort()
    .join("|");
  const hasPublishedMemory =
    (rootView?.total ?? 0) > 0 || Object.values(viewResults).some(results => results.total > 0);
  const viewsAreLoading =
    Boolean(manifest?.root_view_id && !activeRun) && loadedViewManifestId !== manifest?.id;
  const partialRun =
    manifest?.latest_run?.status === "failed" &&
    loadedViewManifestId === manifest.id &&
    hasPublishedMemory
      ? manifest.latest_run
      : null;

  const loadOverview = useCallback(async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!silent) setIsInitialLoading(true);
    try {
      const nextOverview = await getMemoryOverview();
      setOverview(nextOverview);
      setError(null);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load Memory");
    } finally {
      if (!silent) setIsInitialLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  useEffect(() => {
    const pollingRunId = activeRunId ?? anyActiveRunId;
    if (!pollingRunId) return;
    const intervalId = window.setInterval(() => {
      void loadOverview({ silent: true });
    }, 1500);
    return () => window.clearInterval(intervalId);
  }, [activeRunId, anyActiveRunId, loadOverview]);

  useEffect(() => {
    if (agentChangeBaseline === null) return;
    if (manifestRevision !== agentChangeBaseline) {
      setAgentChangeBaseline(null);
      return;
    }
    const intervalId = window.setInterval(() => {
      void loadOverview({ silent: true });
    }, 5000);
    const timeoutId = window.setTimeout(() => setAgentChangeBaseline(null), 15 * 60 * 1000);
    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [agentChangeBaseline, loadOverview, manifestRevision]);

  useEffect(() => {
    if (!manifest?.root_view_id || activeRun) {
      setRootView(null);
      setViewResults({});
      setLoadedViewManifestId(null);
      return;
    }

    let isCurrent = true;
    setRootView(null);
    setViewResults({});
    setLoadedViewManifestId(null);
    const loadViews = async () => {
      try {
        const nextRoot = await getMemoryView({ viewId: manifest.root_view_id as string, limit: 1 });
        if (!isCurrent) return;
        setRootView(nextRoot);
        const descendants = await loadMemoryViewTree(nextRoot);
        if (!isCurrent) return;
        setViewResults(descendants);
      } catch (loadError) {
        if (isCurrent) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load this Memory");
        }
      } finally {
        if (isCurrent) setLoadedViewManifestId(manifest.id);
      }
    };
    void loadViews();
    return () => {
      isCurrent = false;
    };
  }, [activeRun, manifest?.id, manifest?.root_view_id, manifest?.updated_at]);

  useEffect(() => {
    if (!selectedEntityId) {
      setEntityDetail(null);
      return;
    }
    let isCurrent = true;
    setEntityDetail(null);
    getMemoryEntity(selectedEntityId)
      .then(detail => {
        if (isCurrent) setEntityDetail(detail);
      })
      .catch(loadError => {
        if (isCurrent) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load this item");
          setSelectedEntityId(null);
        }
      });
    return () => {
      isCurrent = false;
    };
  }, [selectedEntityId]);

  const startTracking = async () => {
    setIsStarting(true);
    setError(null);
    try {
      const response = await trackCustomerIntelligence();
      setSelectedManifestId(response.manifest.id);
      await loadOverview({ silent: true });
    } catch (startError) {
      setError(
        startError instanceof Error
          ? startError.message
          : "Unable to start tracking customer intelligence"
      );
    } finally {
      setIsStarting(false);
    }
  };

  const openAgentDialog = ({
    mode,
    manifest: dialogManifest = null,
  }: {
    mode: "create" | "customize" | "edit";
    manifest?: MemoryManifest | null;
  }) => {
    setAgentDialog({
      mode,
      manifest: dialogManifest,
      hasExistingDraft: Boolean(localStorageModule.readMemAgentComposerDraft()),
    });
  };

  const continueInAgent = (request: string) => {
    const prompt = buildMemoryAgentPrompt({
      mode: agentDialog?.mode ?? "create",
      manifest: agentDialog?.manifest ?? null,
      request,
    });
    const paragraph = document.createElement("p");
    paragraph.textContent = prompt;
    localStorageModule.requestMemAgentComposerDraft(paragraph.outerHTML);
    setAgentChangeBaseline(manifestRevision);
    setAgentDialog(null);
    store.floatingAgent.requestOpenPanel();
  };

  const openManifest = (manifestId: string) => {
    setSelectedManifestId(manifestId);
    setSelectedViewId(null);
    setSelectedEntityId(null);
    setEntityDetail(null);
  };

  const openType = (typeKey: string) => {
    const typeView = overview?.views.find(view => view.query.type_key === typeKey);

    if (!typeView?.manifest_id) return;

    setSelectedManifestId(typeView.manifest_id);
    setSelectedViewId(typeView.id);
    setSelectedEntityId(null);
    setEntityDetail(null);
  };

  const returnHome = () => {
    setSelectedManifestId(null);
    setSelectedViewId(null);
    setSelectedEntityId(null);
    setEntityDetail(null);
  };

  const syncNow = async () => {
    if (!manifest) return;
    setIsSyncing(true);
    setError(null);
    try {
      if (manifest.status === "active") {
        await backfillMemoryManifest(manifest.id);
      } else {
        await updateMemoryManifestStatus({ manifestId: manifest.id, status: "active" });
      }
      await loadOverview({ silent: true });
    } catch (syncError) {
      setError(syncError instanceof Error ? syncError.message : "Unable to sync Memory");
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <AgentPageLayout activeTab={AgentSurfaceNavigationActiveTab.Memory}>
      {error ? (
        <ErrorBanner role="alert">
          <MDSIcon kind={MDSIconKind.CircleExclamation} size={16} />
          <MDSText color={MDSTextColor.Danger} variant={MDSTextVariant.TextS}>
            {error}
          </MDSText>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Transparent}
            onPress={() => void loadOverview()}
          >
            Try again
          </MDSButton>
        </ErrorBanner>
      ) : null}

      {partialRun ? (
        <RunWarningBanner role="alert">
          <MDSIcon kind={MDSIconKind.Warning} size={16} />
          <RunWarningCopy>
            <MDSText variant={MDSTextVariant.TextS}>
              {partialRun.error_message || "Some source notes need another pass."}
            </MDSText>
            <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.TextXs}>
              The successfully processed Memory is available below.
            </MDSText>
          </RunWarningCopy>
          <MDSButton
            size={MDSButtonSize.Small}
            variant={MDSButtonVariant.Transparent}
            isLoading={isSyncing}
            onPress={syncNow}
          >
            Try again
          </MDSButton>
        </RunWarningBanner>
      ) : null}

      {isInitialLoading && !overview ? (
        <InitialLoading aria-label="Loading Memory">
          <MDSSkeleton width="28%" height={28} />
          <MDSSkeleton width="64%" height={16} />
          <SkeletonGrid>
            <MDSSkeleton width="100%" height={110} />
            <MDSSkeleton width="100%" height={110} />
            <MDSSkeleton width="100%" height={110} />
            <MDSSkeleton width="100%" height={110} />
          </SkeletonGrid>
        </InitialLoading>
      ) : !manifest && overview ? (
        <MemoryHome
          overview={overview}
          isStartingCustomerIntelligence={isStarting}
          onCreate={() => openAgentDialog({ mode: "create" })}
          onCustomize={() => openAgentDialog({ mode: "customize" })}
          onOpenManifest={openManifest}
          onOpenType={openType}
          onStartCustomerIntelligence={startTracking}
        />
      ) : activeRun && manifest ? (
        <MemoryLoadingState run={activeRun} manifest={manifest} />
      ) : manifest?.latest_run?.status === "failed" && viewsAreLoading ? (
        <InitialLoading aria-label="Loading available Memory">
          <MDSSkeleton width="28%" height={28} />
          <MDSSkeleton width="65%" height={16} />
          <MDSSkeleton width="100%" height={180} />
        </InitialLoading>
      ) : manifest?.latest_run?.status === "failed" && !partialRun ? (
        <FailedState>
          <MDSIcon kind={MDSIconKind.CircleExclamation} size={28} />
          <MDSText as="h1" variant={MDSTextVariant.HeaderL}>
            {manifest.display_name} needs another pass
          </MDSText>
          <MDSText color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
            {manifest.latest_run.error_message ||
              "Mem Agent could not finish reading your notes. Your definition is safe."}
          </MDSText>
          <MDSButton variant={MDSButtonVariant.Emphasis} isLoading={isSyncing} onPress={syncNow}>
            Try the backfill again
          </MDSButton>
        </FailedState>
      ) : selectedEntityId && manifest ? (
        entityDetail ? (
          <MemoryEntityDetailView
            detail={entityDetail}
            types={overview?.types ?? []}
            backLabel={
              overview?.views.find(view => view.id === selectedViewId)?.display_name ??
              manifest.display_name
            }
            onBack={() => setSelectedEntityId(null)}
            onChange={setEntityDetail}
            onSelectEntity={setSelectedEntityId}
          />
        ) : (
          <InitialLoading aria-label="Loading Memory item">
            <MDSSkeleton width="38%" height={28} />
            <MDSSkeleton width="72%" height={16} />
            <MDSSkeleton width="100%" height={180} />
          </InitialLoading>
        )
      ) : overview && manifest ? (
        <MemoryDashboard
          overview={overview}
          manifest={manifest}
          rootView={rootView}
          viewResults={viewResults}
          selectedViewId={selectedViewId}
          isSyncing={isSyncing}
          onSelectView={setSelectedViewId}
          onSelectEntity={setSelectedEntityId}
          onBack={returnHome}
          onEdit={() => openAgentDialog({ mode: "edit", manifest })}
          onSync={syncNow}
        />
      ) : null}
      <MemoryAgentDialog
        isOpen={agentDialog !== null}
        mode={agentDialog?.mode ?? "create"}
        manifestName={agentDialog?.manifest?.display_name ?? null}
        hasExistingDraft={agentDialog?.hasExistingDraft ?? false}
        onClose={() => setAgentDialog(null)}
        onSubmit={continueInAgent}
      />
    </AgentPageLayout>
  );
};

const buildMemoryAgentPrompt = ({
  mode,
  manifest,
  request,
}: {
  mode: "create" | "customize" | "edit";
  manifest: MemoryManifest | null;
  request: string;
}): string => {
  if (mode === "edit" && manifest) {
    return `Update my existing tracked Memory view "${manifest.display_name}" (key: ${manifest.key}). First inspect its current definition. Preserve every existing type, relationship, and view I did not ask to remove. I want: ${request}`;
  }

  if (mode === "customize") {
    return `Update my tracked Memory system. First call list_tracked_memory and inspect all current definitions. Preserve every existing type, relationship, and view I did not ask to remove. I want: ${request}. Reuse canonical types across views when appropriate.`;
  }

  return `Create a new tracked Memory view for me. I want Memory to continuously track: ${request}. Design canonical entity types, relationships, and useful views, backfill my notes, and keep it current.`;
};

const loadMemoryViewTree = async (
  root: MemoryViewResults
): Promise<Record<string, MemoryViewResults>> => {
  const resultsById: Record<string, MemoryViewResults> = {};
  const visitedViewIds = new Set([root.view.id]);
  let pendingViews = root.children;

  while (pendingViews.length > 0) {
    const nextViews = pendingViews.filter(view => !visitedViewIds.has(view.id));
    if (nextViews.length === 0) break;
    nextViews.forEach(view => visitedViewIds.add(view.id));

    const nextResults = await Promise.all(
      nextViews.map(view => getMemoryView({ viewId: view.id, limit: 4 }))
    );
    nextResults.forEach(results => {
      resultsById[results.view.id] = results;
    });
    pendingViews = nextResults.flatMap(results => results.children);
  }

  return resultsById;
};

const ErrorBanner = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.input.default.danger};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};

  > :nth-of-type(2) {
    flex: 1;
  }
`;

const RunWarningBanner = styled(ErrorBanner)`
  border-color: ${({ theme }) => theme.colors.border.surface};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const RunWarningCopy = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const InitialLoading = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[8]} 0;
`;

const SkeletonGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing[3]};
  grid-template-columns: repeat(4, minmax(0, 1fr));

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const FailedState = styled.section`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[4]};
  margin: auto;
  max-width: 560px;
  padding: ${({ theme }) => theme.spacing[10]} 0;
`;
